import React, { useState, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Divider,
  Alert,
  CircularProgress,
} from "@mui/material";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import EditIcon from "@mui/icons-material/Edit";

import { PATIENT_STATUS_OPTIONS, getDecisionColor } from "../../utils/codingOpStorage";
import { CodingOpContext } from "../../../../context/CodingOpContext/CodingOpContext";

export default function FinishProcessDialog({
  open,
  onClose,
  opportunities,
  overrides,
  onConfirm,
  patient,
}) {
  const { updatePatientStatus, addPatientComment } = useContext(CodingOpContext);
  const [statusId, setStatusId] = useState("");
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleConfirm = async () => {
    if (statusId === "" || statusId === null) return;
    if (!patient?.cipId) {
      setError("No patient CIP ID found");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Call backend to update patient status
      const success = await updatePatientStatus(patient.cipId, statusId);

      if (success) {
        // Save comment to backend if provided
        if (comment && comment.trim()) {
          try {
            await addPatientComment(patient.cipId, comment);
          } catch (commentErr) {
            console.error("Error saving patient comment:", commentErr);
            // Continue even if comment fails - status was saved
          }
        }

        // Get the status label for the callback
        const selectedStatus = PATIENT_STATUS_OPTIONS.find((opt) => opt.id === statusId);
        onConfirm({ status: selectedStatus?.label || "", statusId, comment });
        setStatusId("");
        setComment("");
      } else {
        setError("Error updating patient status. Please try again.");
      }
    } catch (err) {
      console.error("Error updating patient status:", err);
      setError("Error updating patient status. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setStatusId("");
    setComment("");
    setError(null);
    onClose();
  };

  // Get final decision for each opportunity (override or AI)
  const getFinalDecision = (op) => {
    const opId = op.id;
    if (overrides[opId]) {
      return {
        decision: overrides[opId].decision,
        isOverridden: true,
        comment: overrides[opId].comment,
      };
    }
    return {
      decision: op.decision,
      isOverridden: false,
      comment: null,
    };
  };

  // Count decisions by type
  const decisionCounts = (opportunities || []).reduce(
    (acc, op) => {
      const { decision } = getFinalDecision(op);
      const key = decision?.toLowerCase() || "unknown";
      if (key.includes("approve")) acc.approved++;
      else if (key.includes("reject")) acc.rejected++;
      else acc.other++;
      return acc;
    },
    { approved: 0, rejected: 0, other: 0 }
  );

  const overrideCount = Object.keys(overrides || {}).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <DoneAllIcon color="success" />
          <span>Finish Process</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Box>
          {/* Patient Info */}
          {patient && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: "grey.50" }}>
              <Typography variant="subtitle2" color="text.secondary">
                Patient
              </Typography>
              <Typography fontWeight={700}>
                {patient.memberFullName || "Unknown Patient"}
              </Typography>
              {patient.dateOfBirth && (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  DOB: {patient.dateOfBirth}
                </Typography>
              )}
            </Paper>
          )}

          {/* Summary Stats */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Summary
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label={`${opportunities?.length || 0} Coding Opportunities`}
                variant="outlined"
              />
              <Chip
                label={`${decisionCounts.approved} Approved`}
                color="success"
                variant="outlined"
              />
              <Chip
                label={`${decisionCounts.rejected} Rejected`}
                color="error"
                variant="outlined"
              />
              <Chip
                label={`${decisionCounts.other} Other`}
                variant="outlined"
              />
              {overrideCount > 0 && (
                <Chip
                  icon={<EditIcon />}
                  label={`${overrideCount} Modified`}
                  color="secondary"
                  variant="outlined"
                />
              )}
            </Stack>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Opportunities Table */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Coding Opportunities Final Status
          </Typography>
          <Paper variant="outlined" sx={{ mb: 3, maxHeight: 300, overflow: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Final Decision</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(opportunities || []).map((op, idx) => {
                  const { decision, isOverridden, comment: opComment } = getFinalDecision(op);
                  return (
                    <TableRow key={op.id || idx} hover>
                      <TableCell>
                        <Typography fontWeight={600} variant="body2">
                          {op.code || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                          {op.description?.slice(0, 40)}...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={decision || "—"}
                          color={getDecisionColor(decision)}
                        />
                      </TableCell>
                      <TableCell>
                        {isOverridden ? (
                          <Chip
                            size="small"
                            icon={<EditIcon />}
                            label="Modified"
                            color="secondary"
                            variant="outlined"
                          />
                        ) : (
                          <Chip
                            size="small"
                            label="AI"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>

          <Divider sx={{ my: 2 }} />

          {/* Patient Status Selection */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            Assign Patient Status
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="patient-status-label">Patient Status</InputLabel>
            <Select
              labelId="patient-status-label"
              value={statusId}
              label="Patient Status"
              onChange={(e) => setStatusId(e.target.value)}
              disabled={saving}
            >
              {PATIENT_STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Comment (optional)"
            multiline
            rows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add any additional notes..."
            disabled={saving}
            sx={{ mb: 2 }}
          />

          {statusId === "" && (
            <Alert severity="info">
              Please select a patient status to complete the process.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <AssignmentTurnedInIcon />}
          onClick={handleConfirm}
          disabled={statusId === "" || saving}
        >
          {saving ? "Saving..." : "Complete Process"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
